import type { CameraConfig } from '../config/camera.config';
import { damp, DEG_TO_RAD, wrapAngle } from '../utils/math';

/** Horizontal world point. */
export interface PlanarPoint {
  x: number;
  z: number;
}

/**
 * Keeps the camera pivot continuous where the track frame jumps. At a junction turn the
 * path re-anchors on the exit leg: the frame point under the runner and the heading jump
 * (every lane turns at its own corner), the runner itself does not. Without this, the
 * pivot – lane position on the frame – jumps up to 3 m for the outer lanes and the camera
 * briefly ends up beside the road. The step is kept as an offset that fades out.
 * Pure logic (no engine) – the rig feeds it each frame.
 */
export class PivotContinuity {
  private offsetX = 0;
  private offsetZ = 0;
  /** Previous frame's inputs (valid after the first frame since reset). */
  private readonly last = { pivotX: 0, pivotZ: 0, runnerX: 0, runnerZ: 0, yaw: 0 };
  private hasLast = false;

  constructor(private readonly config: CameraConfig['turnFollow']) {}

  reset(): void {
    this.offsetX = 0;
    this.offsetZ = 0;
    this.hasLast = false;
  }

  /**
   * `pivot`: the rig's pivot from the current track frame, `runner`: the runner's world
   * position (continuous through turns), `frameYaw`: the track heading. Returns the
   * continuous pivot in `out`.
   */
  apply(dt: number, pivot: Readonly<PlanarPoint>, runner: Readonly<PlanarPoint>, frameYaw: number, out: PlanarPoint): PlanarPoint {
    this.offsetX = damp(this.offsetX, 0, this.config.recoveryDamping, dt);
    this.offsetZ = damp(this.offsetZ, 0, this.config.recoveryDamping, dt);
    const last = this.last;
    if (this.hasLast && Math.abs(wrapAngle(frameYaw - last.yaw)) > this.config.jumpDegrees * DEG_TO_RAD) {
      // Where the pivot would be had the frame not jumped: moved along with the runner.
      this.offsetX += last.pivotX + (runner.x - last.runnerX) - pivot.x;
      this.offsetZ += last.pivotZ + (runner.z - last.runnerZ) - pivot.z;
    }
    last.pivotX = pivot.x;
    last.pivotZ = pivot.z;
    last.runnerX = runner.x;
    last.runnerZ = runner.z;
    last.yaw = frameYaw;
    this.hasLast = true;
    out.x = pivot.x + this.offsetX;
    out.z = pivot.z + this.offsetZ;
    return out;
  }
}
